import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AlertTriangle } from 'lucide-react';
import { apiClient } from '../utils/api';
import { getActs } from '../services';
import { useToast } from '../components/Toast';
import { RootState } from '../store';
import { AppointmentConsultationView } from '../components/appointment/AppointmentConsultationView';
import type {
  AppointmentConsultationData,
  ConsentByActId,
  ConsultationAct,
} from '../components/appointment/appointmentConsultationTypes';
import { normalizeAppointmentConsultationData } from '../components/appointment/appointmentConsultationUtils';

export function AppointmentViewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showToast, ToastComponent } = useToast();

  const [appointment, setAppointment] = useState<AppointmentConsultationData | null>(null);
  const [acts, setActs] = useState<ConsultationAct[]>([]);
  const [actsLoading, setActsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [docs, setDocs] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [consentByActId, setConsentByActId] = useState<ConsentByActId>({});

  const authUser = useSelector((state: RootState) => state.auth.user);
  const signerName = authUser
    ? (
        `${(authUser as any).firstName || ''} ${(authUser as any).lastName || ''}`.trim() ||
        (authUser as any).name ||
        ''
      )
    : '';

  const loadDocs = useCallback(async (appointmentId: string) => {
    try {
      setDocsLoading(true);
      const resp = await apiClient(`/api/appointments/${appointmentId}/documents`);
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.success) setDocs(data.data || []);
    } catch {
      // silently ignore
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const loadActs = useCallback(async (appointmentId: string) => {
    try {
      setActsLoading(true);
      const [actsResult, servicesResp] = await Promise.all([
        getActs({ appointmentId }),
        apiClient('/api/admin/services?limit=1000'),
      ]);

      let serviceMap: Record<string, string> = {};
      if (servicesResp.ok) {
        const servicesData = await servicesResp.json();
        if (servicesData.success) {
          (servicesData.data?.services || []).forEach((service: any) => {
            serviceMap[service.id] = service.name;
          });
        }
      }

      const sorted = [...(actsResult.acts || [])].sort(
        (a, b) =>
          (a.sequence ?? Number.MAX_SAFE_INTEGER) - (b.sequence ?? Number.MAX_SAFE_INTEGER),
      );

      setActs(
        sorted.map((act) => ({
          id: act.id,
          sequence: act.sequence,
          status: act.status,
          serviceId: act.serviceId,
          serviceName: serviceMap[act.serviceId] || act.serviceId,
          notes: act.notes,
          performedAt: act.performedAt,
          demandDetails: act.demandDetails,
          piercingRecord: act.piercingRecord as ConsultationAct['piercingRecord'],
        })),
      );
    } catch (err) {
      console.error('Failed to load acts:', err);
      setActs([]);
    } finally {
      setActsLoading(false);
    }
  }, []);

  const fetchAppointment = useCallback(
    async (appointmentId: string) => {
      try {
        setLoading(true);
        const response = await apiClient(`/api/admin/appointments/${appointmentId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch appointment');
        }
        const data = await response.json();
        if (data.success) {
          const loaded = normalizeAppointmentConsultationData(data.data.appointment);
          setAppointment(loaded);
          if (
            loaded?.id &&
            appointmentId.length === 4 &&
            loaded.id !== appointmentId
          ) {
            navigate(`/appointments/${loaded.id}`, { replace: true });
          }
        } else {
          setError(data.message || 'Failed to load appointment');
        }
      } catch (err) {
        console.error('Fetch appointment error:', err);
        setError('Failed to load appointment data');
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (!id) return;
    fetchAppointment(id);
    loadActs(id);
    loadDocs(id);
  }, [id, fetchAppointment, loadActs, loadDocs]);

  useEffect(() => {
    if (!acts.length) {
      setConsentByActId({});
      return;
    }
    let cancelled = false;
    (async () => {
      const next: ConsentByActId = {};
      await Promise.all(
        acts.map(async (act) => {
          try {
            const resp = await apiClient(`/api/admin/acts/${act.id}/contract`);
            if (!resp.ok) return;
            const data = await resp.json();
            if (data.success && data.data?.type === 'PIERCING_CONSENT') {
              next[act.id] = data.data;
            }
          } catch {
            /* ignore */
          }
        }),
      );
      if (!cancelled) setConsentByActId(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [acts]);

  const openConsentPdf = useCallback(
    async (actId: string) => {
      try {
        const res = await apiClient(`/api/admin/acts/${actId}/consent-pdf`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'PDF indisponible');
        }
        return await res.blob();
      } catch (e: any) {
        showToast(e?.message || 'Impossible d’ouvrir le PDF', 'error', 5000);
        return null;
      }
    },
    [showToast],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement du rendez-vous...</span>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">
              {error || 'Rendez-vous non trouvé'}
            </span>
          </div>
        </div>
        <button onClick={() => navigate('/appointments')} className="mt-4 btn-secondary">
          Retour à la liste des rendez-vous
        </button>
      </div>
    );
  }

  return (
    <AppointmentConsultationView
      appointment={appointment}
      acts={acts}
      actsLoading={actsLoading}
      consentByActId={consentByActId}
      docs={docs}
      docsLoading={docsLoading}
      listPath="/appointments"
      showHelpIcon
      allowDocumentSign
      allowDocumentView
      onOpenConsentPdf={openConsentPdf}
      signerName={signerName}
      onReloadDocs={() => {
        if (id) void loadDocs(id);
      }}
      ToastComponent={<ToastComponent />}
    />
  );
}
